import { FC, useEffect, useState } from 'react'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { Store, applyMiddleware, createStore } from 'redux'
// @ts-ignore
import thunk from 'redux-thunk'
import { Provider } from 'react-redux'
import { loadState, saveState } from 'lib/session-storage'
import { mergeRegistry, mergeUser } from 'registry/actions'
import { track, withContextualizedTracking } from 'lib/analytics'
import OnboardingWizard from './OnboardingWizard'
import { RECAPTCHA_V3_SITE_KEY } from 'shared/constants'
import OnboardingAppContext, {
  OnboardingAppContextValues,
} from './OnboardingAppContext'
import reducer, { getOnboarding, getRegistry, getUser } from '../reducers'
import { setInitialRegItems } from '../actions'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'

const middlewares = [thunk]
const createStoreWithMiddleware = applyMiddleware(...middlewares)(createStore)

function getEventLocation() {
  if (typeof window !== 'undefined') {
    return new URLSearchParams(window.location.search).get('loc')
  }
}

interface OnboardingAppProps {
  seedUser: {
    email?: string
    recommended_by?: string
  }
  registry: any
  actEx2Mobile: string
  initialRegItems: any
  path: string
  onboardingFlow: string
  ssoEnabled: boolean
  context: OnboardingAppContextValues
}

const OnboardingApp: FC<OnboardingAppProps> = ({
  seedUser,
  registry,
  initialRegItems,
  path,
  onboardingFlow,
  ssoEnabled,
  actEx2Mobile,
  context,
}) => {
  const [currentUser] = useCurrentUser()
  const [store] = useState<Store<unknown>>(
    createStoreWithMiddleware(reducer, loadState())
  )

  useEffect(() => {
    // Persist redux state to sessionStorage so that user does not lose progress when refreshing the page.
    store.subscribe(() => {
      const state = store.getState()
      const user = getUser(state)
      const registry = getRegistry(state)
      const onboarding = getOnboarding(state)
      // if we have not yet created the user in the database persist these values to sessions storage
      if (!user || !user.id) {
        saveState({ onboarding, registry, user })
      }
    })

    if (registry) {
      store.dispatch(mergeRegistry(registry))
    }

    if (initialRegItems) {
      store.dispatch(setInitialRegItems(initialRegItems))
    }

    track.signUpStarted({
      eventLocation: getEventLocation() || 'Onboarding',
    })
    if (typeof window !== undefined && window.fbq) {
      window.fbq('trackCustom', 'RegistryLead')
    }
  }, [])

  useEffect(() => {
    if (currentUser) {
      store.dispatch(
        mergeUser({
          ...seedUser,
          ...currentUser,
        })
      )
    }
  }, [currentUser])

  const currentRegistryRole = currentUser?.currentRegistry?.currentUserRole

  return (
    <Provider store={store}>
      <OnboardingAppContext.Provider value={context}>
        <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_V3_SITE_KEY}>
          <OnboardingWizard
            actEx2bMobile={actEx2Mobile}
            currentRegistryRole={currentRegistryRole}
            initialPath={path}
            onboardingFlow={onboardingFlow}
            registry={registry}
            ssoEnabled={ssoEnabled}
          />
        </GoogleReCaptchaProvider>
      </OnboardingAppContext.Provider>
    </Provider>
  )
}

// todo add to location
export default withContextualizedTracking({
  eventLocation: getEventLocation() || track.EventLocation.ONBOARDING,
})(OnboardingApp)
